// src/services/goalRules.js
//
// Rules an admin sets for each goal circle (Phase 2): how coordinators' weekly
// report numbers turn into goal points.
//
//   "Every N of [categories] in [scope] = P point(s)"
//     categories: one or more of Pre-screen, Screening, Screen fail,
//                 Enrolled/Randomized, Follow-up
//     scope:      any study | these studies | studies of these types (DISEASE)
//
// One rule set per goal ("enrollments" = Randomizations, "screenings"), saved
// with the month it starts from; a month uses the latest set starting on or
// before it, so rules carry on until changed.
//
// Rules are checked top to bottom and each counted item is used once (the
// order matters; specific rules go first): see evaluateRules. Points only
// reach the goal counts when an admin approves them (goalPointService.js).
//
// Pure and Firebase-free so it is unit-tested (goalRules.test.js).

import { REPORT_STATUSES, REPORT_FORMAT, countValue, reportPeriodCounts } from "./weeklyReportModel";
import { reportCoordinatorName } from "./weeklyReportCoordinator";

export const GOAL_RULE_METRICS = Object.freeze({
  enrollments: { id: "enrollments", label: "Randomizations", unit: "randomization point" },
  screenings: { id: "screenings", label: "Screenings", unit: "screening point" },
});

// The things a rule can count: the weekly report's statuses.
export const RULE_CATEGORIES = REPORT_STATUSES.map((s) => ({ id: s.id, label: s.label, field: s.field }));
const CATEGORY_IDS = new Set(RULE_CATEGORIES.map((c) => c.id));
const PLURAL = {
  preScreen: "pre-screens",
  screening: "screenings",
  screenFail: "screen fails",
  enrolled: "enrollments/randomizations",
  followUp: "follow-ups",
};

export const SCOPE_TYPES = Object.freeze({ ANY: "any", STUDIES: "studies", TYPES: "types" });

const str = (v) => String(v ?? "").trim();
const wholeAtLeastOne = (v) => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n >= 1 ? n : 0;
};

/** A short random id for a rule. */
export const newRuleId = () => `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/** A fresh rule for the editor. */
export const blankRule = () => ({
  id: newRuleId(),
  count: 1,
  categories: [],
  scope: { type: SCOPE_TYPES.ANY, studyDocIds: [], types: [] },
  points: 1,
});

/** Any stored rule -> a clean rule (unknown categories dropped, numbers whole). */
export const normalizeRule = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const type = Object.values(SCOPE_TYPES).includes(r.scope?.type) ? r.scope.type : SCOPE_TYPES.ANY;
  return {
    id: str(r.id) || newRuleId(),
    count: wholeAtLeastOne(r.count),
    categories: [...new Set((Array.isArray(r.categories) ? r.categories : []).filter((c) => CATEGORY_IDS.has(c)))],
    scope: {
      type,
      studyDocIds: [...new Set((Array.isArray(r.scope?.studyDocIds) ? r.scope.studyDocIds : []).map(str).filter(Boolean))],
      types: [...new Set((Array.isArray(r.scope?.types) ? r.scope.types : []).map(str).filter(Boolean))],
    },
    points: wholeAtLeastOne(r.points),
  };
};

export const normalizeRules = (rules) => (Array.isArray(rules) ? rules.map(normalizeRule) : []);

/** What's wrong with a rule, if anything (blocks saving). */
export const validateRule = (rule) => {
  const r = normalizeRule(rule);
  const errors = [];
  if (!r.count) errors.push("How many: at least 1.");
  if (r.categories.length === 0) errors.push("Pick what to count.");
  if (r.scope.type === SCOPE_TYPES.STUDIES && r.scope.studyDocIds.length === 0) errors.push("Pick at least one study.");
  if (r.scope.type === SCOPE_TYPES.TYPES && r.scope.types.length === 0) errors.push("Pick at least one study type.");
  if (!r.points) errors.push("Points: at least 1.");
  return errors;
};

const orList = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;

const studyName = (id, studiesById) => {
  const s = studiesById?.get ? studiesById.get(id) : studiesById?.[id];
  if (!s) return "a study no longer listed";
  return [s.STUDY_ID, s.NAME].filter(Boolean).join(" – ") || "an unnamed study";
};

/** The rule as one sentence: "Every 3 screenings or screen fails, in any study = 1 point". */
export const ruleText = (rule, studiesById) => {
  const r = normalizeRule(rule);
  const what = r.categories.length ? orList(r.categories.map((c) => PLURAL[c])) : "(nothing picked)";
  const where =
    r.scope.type === SCOPE_TYPES.STUDIES
      ? `in ${orList(r.scope.studyDocIds.map((id) => studyName(id, studiesById))) || "(no study picked)"}`
      : r.scope.type === SCOPE_TYPES.TYPES
      ? `in ${orList(r.scope.types) || "(no type picked)"} studies`
      : "in any study";
  return `Every ${r.count || "?"} ${what}, ${where} = ${r.points || "?"} point${r.points === 1 ? "" : "s"}`;
};

/** Studies a "types" scope covers today (study DISEASE in the picked types). */
export const studiesOfTypes = (types = [], studies = []) => {
  const wanted = new Set(types);
  return studies.filter((s) => wanted.has(str(s.DISEASE)));
};

/** The rule-set id for a goal and the month it starts from. */
export const ruleSetId = (metric, monthId) => `${metric}__${monthId}`;

/**
 * The rules in force for a month: the set with the latest `effectiveFrom` on
 * or before it. Returns { effectiveFrom, rules } or null (no rules yet).
 */
export const rulesForMonth = (sets = [], monthId) => {
  const usable = sets
    .filter((s) => s && /^\d{4}-\d{2}$/.test(String(s.effectiveFrom)) && s.effectiveFrom <= monthId)
    .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1));
  if (!usable.length) return null;
  return { effectiveFrom: usable[0].effectiveFrom, rules: normalizeRules(usable[0].rules) };
};

// ---------------------------------------------------------------------------
// Turning weekly reports into points (Step 2)
// ---------------------------------------------------------------------------

const toMs = (ts) =>
  ts?.toMillis ? ts.toMillis() : typeof ts === "number" ? ts : ts instanceof Date ? ts.getTime() : 0;

/**
 * One unit per counted thing in the reports, in date order (report period,
 * then submit time): { reportId, weekOf, studyDocId | null, category }.
 * Only new-format reports count. Numbers not tied to a study (pre-screens;
 * interim staging reports also had follow-ups) get studyDocId null.
 */
export const buildUnits = (reports = []) => {
  const ordered = reports
    .filter((r) => r && r.format === REPORT_FORMAT)
    .sort(
      (a, b) =>
        String(a.periodStart || "").localeCompare(String(b.periodStart || "")) ||
        toMs(a.submittedAt) - toMs(b.submittedAt) ||
        String(a.id).localeCompare(String(b.id))
    );
  const units = [];
  const push = (r, studyDocId, category, n) => {
    for (let i = 0; i < n; i += 1) units.push({ reportId: r.id, weekOf: r.weekOf || "", studyDocId, category });
  };
  ordered.forEach((r) => {
    (r.studies || []).forEach((st) =>
      RULE_CATEGORIES.forEach((c) => push(r, st.studyDocId || null, c.id, countValue(st[c.field])))
    );
    const loose = reportPeriodCounts(r);
    push(r, null, "preScreen", loose.preScreens);
    push(r, null, "followUp", loose.followUps);
  });
  return units;
};

/** Does this unit count for this rule? Units with no study only match "any study". */
export const ruleMatchesUnit = (rule, unit, studiesById) => {
  if (!rule.categories.includes(unit.category)) return false;
  if (rule.scope.type === SCOPE_TYPES.ANY) return true;
  if (!unit.studyDocId) return false;
  if (rule.scope.type === SCOPE_TYPES.STUDIES) return rule.scope.studyDocIds.includes(unit.studyDocId);
  const study = studiesById?.get ? studiesById.get(unit.studyDocId) : studiesById?.[unit.studyDocId];
  return !!study && rule.scope.types.includes(str(study.DISEASE));
};

/**
 * Apply the rules, top to bottom, to the units. Each rule walks the unused
 * units it matches; every `count` of them earns `points`, credited to the week
 * of the unit that completed it, and those units are used up. An unfinished
 * bundle isn't used, so its units stay free for later rules. Incomplete
 * (invalid) rules are skipped.
 * Returns { points, weekly: { weekOf: n }, breakdown: [{ ruleId, points, matched, leftover }] }.
 */
export const evaluateRules = (rules = [], units = [], studiesById) => {
  const used = new Array(units.length).fill(false);
  const weekly = {};
  const breakdown = [];
  let points = 0;
  normalizeRules(rules).forEach((rule) => {
    if (validateRule(rule).length) {
      breakdown.push({ ruleId: rule.id, points: 0, matched: 0, leftover: 0, skipped: true });
      return;
    }
    let bundle = [];
    let rulePoints = 0;
    let matched = 0;
    units.forEach((u, i) => {
      if (used[i] || !ruleMatchesUnit(rule, u, studiesById)) return;
      matched += 1;
      bundle.push(i);
      if (bundle.length === rule.count) {
        bundle.forEach((j) => {
          used[j] = true;
        });
        rulePoints += rule.points;
        weekly[u.weekOf] = (weekly[u.weekOf] || 0) + rule.points;
        bundle = [];
      }
    });
    points += rulePoints;
    breakdown.push({ ruleId: rule.id, points: rulePoints, matched, leftover: bundle.length });
  });
  return { points, weekly, breakdown };
};

/**
 * Points per coordinator for one month's reports (new-format, same month),
 * under one rule set. Returns a Map: coordinator name -> { coordinator, points,
 * weekly, breakdown, reportIds, periodStart, periodEnd }.
 */
export const computeMonthPoints = ({ reports = [], rules = [], studiesById }) => {
  const byCoordinator = new Map();
  reports
    .filter((r) => r && r.format === REPORT_FORMAT)
    .forEach((r) => {
      const name = reportCoordinatorName(r) || "Unknown";
      if (!byCoordinator.has(name)) byCoordinator.set(name, []);
      byCoordinator.get(name).push(r);
    });
  const out = new Map();
  byCoordinator.forEach((list, coordinator) => {
    const result = evaluateRules(rules, buildUnits(list), studiesById);
    const starts = list.map((r) => r.periodStart).filter(Boolean).sort();
    const ends = list.map((r) => r.periodEnd).filter(Boolean).sort();
    out.set(coordinator, {
      coordinator,
      ...result,
      reportIds: list.map((r) => r.id),
      periodStart: starts[0] || "",
      periodEnd: ends[ends.length - 1] || "",
    });
  });
  return out;
};

/**
 * What's new since the admin's last decision for a coordinator + goal + month.
 * totals: { approved, dismissed, decidedWeekly: { weekOf: n } } (or nothing).
 * The new points go to the weeks that gained points, earliest first. If the
 * computed points dropped below what was already decided (rules changed or a
 * report was deleted), `behindBy` says by how much; nothing is subtracted.
 */
export const pendingPoints = (computed, totals) => {
  const decided = countValue(totals?.approved) + countValue(totals?.dismissed);
  const total = computed?.points || 0;
  const newPoints = Math.max(0, total - decided);
  const behindBy = Math.max(0, decided - total);
  const newWeekly = {};
  const weeks = Object.keys(computed?.weekly || {}).sort();
  let left = newPoints;
  weeks.forEach((w) => {
    if (!left) return;
    const gain = Math.max(0, (computed.weekly[w] || 0) - countValue(totals?.decidedWeekly?.[w]));
    const take = Math.min(gain, left);
    if (take > 0) {
      newWeekly[w] = take;
      left -= take;
    }
  });
  // Week credit moved (e.g. a late report for an earlier week): the rest goes
  // on the latest week that earned points.
  if (left > 0 && weeks.length) {
    const last = weeks[weeks.length - 1];
    newWeekly[last] = (newWeekly[last] || 0) + left;
  }
  return { decided, newPoints, newWeekly, behindBy };
};

/** The running-totals doc id for a goal + month + coordinator. */
export const goalTotalsId = (metric, monthId, coordinator) =>
  `${metric}__${monthId}__${encodeURIComponent(String(coordinator || "Unknown"))}`;

const MONTH_RE = /^\d{4}-\d{2}$/;

/**
 * Everything waiting for an admin: one card per coordinator x goal x month
 * with new points (or with fewer points than already decided).
 *   reports:          weekly_reports docs ({ id, ...data }), any months
 *   ruleSetsByMetric: { enrollments: [sets], screenings: [sets] }
 *   totalsById:       { [goalTotalsId]: totals }
 *   roster:           leaderboard names (to flag someone not on it), optional
 */
export const buildGoalPointCards = ({ reports = [], ruleSetsByMetric = {}, studiesById, totalsById = {}, roster }) => {
  const newFormat = reports.filter((r) => r && r.format === REPORT_FORMAT && MONTH_RE.test(String(r.monthId)));
  const months = [...new Set(newFormat.map((r) => r.monthId))].sort().reverse();
  const cards = [];
  Object.keys(GOAL_RULE_METRICS).forEach((metric) => {
    months.forEach((monthId) => {
      const inForce = rulesForMonth(ruleSetsByMetric[metric] || [], monthId);
      if (!inForce || inForce.rules.length === 0) return;
      const computed = computeMonthPoints({
        reports: newFormat.filter((r) => r.monthId === monthId),
        rules: inForce.rules,
        studiesById,
      });
      computed.forEach((c) => {
        const id = goalTotalsId(metric, monthId, c.coordinator);
        const totals = totalsById[id] || null;
        const pending = pendingPoints(c, totals);
        if (pending.newPoints === 0 && pending.behindBy === 0) return;
        cards.push({
          id,
          metric,
          monthId,
          rulesFrom: inForce.effectiveFrom,
          ...c,
          breakdown: c.breakdown.map((b) => ({
            ...b,
            text: ruleText(inForce.rules.find((r) => r.id === b.ruleId), studiesById),
          })),
          ...pending,
          decidedBefore: { approved: countValue(totals?.approved), dismissed: countValue(totals?.dismissed) },
          onRoster: !roster || roster.includes(c.coordinator),
        });
      });
    });
  });
  return cards.sort(
    (a, b) => (a.monthId < b.monthId ? 1 : a.monthId > b.monthId ? -1 : 0) || a.coordinator.localeCompare(b.coordinator)
  );
};
